import React, { forwardRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helper?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Estilo pill de app/obros/lib/widgets/obros_input.dart (label arriba + contenedor redondeado). */
  pill?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helper, leftIcon, rightIcon, pill = false, className = '', required, ...props }, ref) => {
    if (pill) {
      return (
        <div className={className}>
          {label && (
            <label className="flex items-center gap-1 mb-1.5 text-sm font-bold text-onSurface">
              {label}
              {required && <span className="text-danger-600">*</span>}
            </label>
          )}
          <div
            className={`flex items-center gap-2 min-h-[52px] rounded-2xl border bg-surface-dim px-4 ${
              error ? 'border-danger-500' : 'border-outlineVarWarm'
            }`}
          >
            {leftIcon && <span className="text-onSurface-mute">{leftIcon}</span>}
            <input
              ref={ref}
              required={required}
              className="flex-1 bg-transparent py-3 text-onSurface placeholder-onSurface-mute outline-none"
              {...props}
            />
            {rightIcon}
          </div>
          {error && <p className="mt-1 text-sm text-danger-600">{error}</p>}
          {helper && !error && <p className="mt-1 text-sm text-onSurface-mute">{helper}</p>}
        </div>
      );
    }

    return (
      <div className={className}>
        {label && <label className="label">{label}</label>}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            required={required}
            className={`input ${error ? 'input-error' : ''} ${leftIcon ? 'pl-10' : ''} ${rightIcon ? 'pr-10' : ''}`}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-danger-600">{error}</p>}
        {helper && !error && <p className="mt-1 text-sm text-secondary-500">{helper}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
